import { computed } from "vue";
import useCreateComponentConfig from "../../../composables/useCreateComponentConfig.js";
import { ECarouselDesktopWidth, EMobileSlidesVariants } from "../types.js";
import { SIZES } from "@/ui/ts/types/Sizes";
var EElements = /* @__PURE__ */ ((EElements2) => {
  EElements2["ROOT"] = "ROOT";
  EElements2["CONTAINER"] = "CONTAINER";
  EElements2["CAROUSEL"] = "CAROUSEL";
  EElements2["SLIDES_CONTAINER"] = "SLIDES_CONTAINER";
  EElements2["BUTTON_LEFT"] = "BUTTON_LEFT";
  EElements2["BUTTON_RIGHT"] = "BUTTON_RIGHT";
  return EElements2;
})(EElements || {});
export default function useConfig(props, canScrollPrev, canScrollNext) {
  const rootClasses = useRootConfig(props);
  const containerClasses = useContainerConfig(props);
  const carouselClasses = useCarouselConfig(props);
  const slidesContainerConfig = useSlidesContainerConfig(props);
  const buttonClasses = useButtonConfig(props);
  const buttonLeftClasses = useButtonLeftConfig(props, canScrollPrev);
  const buttonRightClasses = useButtonRightConfig(props, canScrollNext);
  return {
    rootClasses,
    containerClasses,
    carouselClasses,
    slidesContainerConfig,
    buttonClasses,
    buttonLeftClasses,
    buttonRightClasses
  };
}
function useRootConfig(props) {
  const { setBase, getClasses } = useCreateComponentConfig();
  setBase(
    "ROOT" /* ROOT */,
    /* tw */
    "group/simple"
  );
  if (props.desktopWidth === ECarouselDesktopWidth.FULL) {
    setBase(
      "ROOT" /* ROOT */,
      /* tw */
      "overflow-hidden"
    );
  }
  if (props.inContainer && props.desktopWidth !== ECarouselDesktopWidth.FULL) {
    setBase(
      "ROOT" /* ROOT */,
      /* tw */
      "max-md:-mx-4 md:max-lg:-mx-6"
    );
  }
  const classes = computed(() => {
    return getClasses("ROOT" /* ROOT */, void 0, void 0, void 0, void 0);
  });
  return classes.value;
}
function useContainerConfig(props) {
  const { setBase, getClasses } = useCreateComponentConfig();
  setBase(
    "CONTAINER" /* CONTAINER */,
    /* tw */
    "container !px-0"
  );
  if (!props.inContainer || props.desktopWidth === ECarouselDesktopWidth.FULL) {
    setBase(
      "CONTAINER" /* CONTAINER */,
      /* tw */
      "lg:!px-6"
    );
  }
  const classes = computed(() => {
    return getClasses("CONTAINER" /* CONTAINER */, void 0, void 0, void 0);
  });
  return classes.value;
}
function useCarouselConfig(props) {
  const { setBase, getClasses } = useCreateComponentConfig();
  setBase(
    "CAROUSEL" /* CAROUSEL */,
    /* tw */
    "max-md:px-4 md:max-lg:px-6"
  );
  if (props.desktopWidth === ECarouselDesktopWidth.GRID) {
    setBase(
      "CAROUSEL" /* CAROUSEL */,
      /* tw */
      "overflow-hidden"
    );
  }
  const classes = computed(() => {
    return getClasses("CAROUSEL" /* CAROUSEL */, void 0, void 0, void 0, void 0);
  });
  return classes.value;
}
function useSlidesContainerConfig(props) {
  const { setBase, addSize, getClasses } = useCreateComponentConfig();
  setBase(
    "SLIDES_CONTAINER" /* SLIDES_CONTAINER */,
    /* tw */
    `flex -mx-1`
  );
  if (props.mobileSlidesVariant === EMobileSlidesVariants.FULLWIDTH) {
    setBase(
      "SLIDES_CONTAINER" /* SLIDES_CONTAINER */,
      /* tw */
      "max-md:-mx-2"
    );
  }
  if (props.tabletSlidesVariant === EMobileSlidesVariants.FULLWIDTH) {
    setBase(
      "SLIDES_CONTAINER" /* SLIDES_CONTAINER */,
      /* tw */
      "md:max-lg:-mx-3"
    );
  }
  addSize(
    "SLIDES_CONTAINER" /* SLIDES_CONTAINER */,
    SIZES.LARGE,
    /* tw */
    `lg:-mx-3`
  );
  const classes = computed(() => {
    return getClasses("SLIDES_CONTAINER" /* SLIDES_CONTAINER */, void 0, props.desktopSize, void 0, void 0);
  });
  return classes.value;
}
function useButtonConfig(props) {
  const { setBase, getClasses } = useCreateComponentConfig();
  setBase(
    "BUTTON_LEFT" /* BUTTON_LEFT */,
    /* tw */
    `hidden lg:flex absolute top-1/2 -translate-y-1/2 transition-opacity duration-200 opacity-0 pointer-events-none`
  );
  const classes = computed(() => {
    return getClasses("BUTTON_LEFT" /* BUTTON_LEFT */, void 0, props.desktopSize, void 0, void 0);
  });
  return classes.value;
}
function useButtonLeftConfig(props, canScrollPrev) {
  const { setBase, addSize, getClasses } = useCreateComponentConfig();
  setBase(
    "BUTTON_LEFT" /* BUTTON_LEFT */,
    /* tw */
    `${props.hoverZoom ? "left-0" : "left-2 -translate-x-1/2"}`
  );
  if (canScrollPrev.value) {
    setBase(
      "BUTTON_LEFT" /* BUTTON_LEFT */,
      /* tw */
      `${props.hoverZoom ? "group-hover/simple:pointer-events-auto group-hover/simple:opacity-100" : "pointer-events-auto opacity-100"}`
    );
  }
  addSize(
    "BUTTON_LEFT" /* BUTTON_LEFT */,
    SIZES.LARGE,
    /* tw */
    `min-[1368px]:-ml-4 min-[1368px]:-translate-x-full`
  );
  const classes = computed(() => {
    return getClasses("BUTTON_LEFT" /* BUTTON_LEFT */, void 0, props.desktopSize, void 0, void 0);
  });
  return classes.value;
}
function useButtonRightConfig(props, canScrollNext) {
  const { setBase, addSize, getClasses } = useCreateComponentConfig();
  setBase(
    "BUTTON_RIGHT" /* BUTTON_RIGHT */,
    /* tw */
    `${props.hoverZoom ? "right-0" : "right-2 translate-x-1/2"}`
  );
  if (canScrollNext.value) {
    setBase(
      "BUTTON_RIGHT" /* BUTTON_RIGHT */,
      /* tw */
      `${props.hoverZoom ? "group-hover/simple:pointer-events-auto group-hover/simple:opacity-100" : "pointer-events-auto opacity-100"}`
    );
  }
  addSize(
    "BUTTON_RIGHT" /* BUTTON_RIGHT */,
    SIZES.LARGE,
    /* tw */
    "min-[1368px]:translate-x-full min-[1368px]:-mr-4"
  );
  const classes = computed(() => {
    return getClasses("BUTTON_RIGHT" /* BUTTON_RIGHT */, void 0, props.desktopSize, void 0, void 0);
  });
  return classes.value;
}
